import { MediaManagerFileSystem } from 'src/app/models/media-manager/media-manager-file-system';
import { Asset } from '../../../models/image/dto/asset';
import { MediaManagerAssetLibraryDto } from '../../../models/image/dto/media-manager-asset-library-dto';
import { ArrayUtils } from '../../../utils/array-utils';
import { findAssetInLibrary } from './asset-library-lookups';

/**
 * Copy server-owned mutable fields from an updated asset onto the canonical asset library entry in place.
 *
 * The asset library is mutated by reference throughout the codebase, so callers rely on the same object identity
 * still being present after a patch. This function centralizes the field list so drift between mutation sites is
 * easier to catch.
 */
export function patchAssetLibraryAsset(oldAsset: Asset, updatedAsset: Asset): void {
  oldAsset.urls = updatedAsset.urls;
  oldAsset.timestamp = updatedAsset.timestamp;
  oldAsset.fileName = updatedAsset.fileName;
  oldAsset.mediaType = updatedAsset.mediaType;
  oldAsset.cachedTime = updatedAsset.cachedTime;
  oldAsset.mediaClass = updatedAsset.mediaClass;
  oldAsset.parentFolderId = updatedAsset.parentFolderId;
  oldAsset.tags = updatedAsset?.tags?.shallowCopy() || [];
  oldAsset.assetLibraryType = updatedAsset.assetLibraryType;
  oldAsset.lastModified = updatedAsset.lastModified;
  oldAsset.bucket = updatedAsset.bucket;
  oldAsset.displayName = updatedAsset.displayName;
  oldAsset.key = updatedAsset.key;
  oldAsset.outputProcessed = updatedAsset.outputProcessed;
}

/** Move an asset to a new parent folder inside the filesystem. */
export function moveAsset(oldAssetId: string, updatedAsset: Asset, filesystem: MediaManagerFileSystem): boolean {
  return filesystem.moveAsset(oldAssetId, updatedAsset);
}

/** Patch asset metadata (name, tags, mediaClass, etc.) inside the filesystem. */
export function patchAssetMetadata(
  oldAssetId: string,
  updatedAsset: Asset,
  filesystem: MediaManagerFileSystem
): boolean {
  return filesystem.patchAssetMetadata(oldAssetId, updatedAsset);
}

/**
 * Reconcile the filesystem and asset library entries for a single asset update. Returns false if the identifier
 * changes (must not happen for an in-place update) or if any filesystem mutation refuses to apply, so the caller
 * can fall back to a full refetch.
 */
export function applyUpdatedAsset(
  currentAsset: Asset,
  updatedAsset: Asset,
  filesystem: MediaManagerFileSystem
): boolean {
  const oldAssetId = currentAsset.getUniqueIdentifier();
  if (oldAssetId !== updatedAsset.getUniqueIdentifier()) {
    return false;
  }
  const parentChanged = currentAsset.parentFolderId !== updatedAsset.parentFolderId;
  const displayNameChanged = currentAsset.displayName !== updatedAsset.displayName;
  const fileNameChanged = currentAsset.fileName !== updatedAsset.fileName;
  const mediaClassChanged = currentAsset.mediaClass !== updatedAsset.mediaClass;
  const mediaTypeChanged = currentAsset.mediaType !== updatedAsset.mediaType;
  const tagsChanged = !ArrayUtils.arraysEqualAsSets(currentAsset.tags, updatedAsset.tags);
  const metadataChanged = displayNameChanged || fileNameChanged || mediaClassChanged || mediaTypeChanged || tagsChanged;
  if (parentChanged && !moveAsset(oldAssetId, updatedAsset, filesystem)) {
    return false;
  }
  if (metadataChanged && !patchAssetMetadata(oldAssetId, updatedAsset, filesystem)) {
    return false;
  }
  patchAssetLibraryAsset(currentAsset, updatedAsset);
  return true;
}

/**
 * Batch counterpart of {@link applyUpdatedAsset}. Bails out at the first failed lookup or mutation so state does
 * not end up half-patched.
 */
export function applyUpdatedAssets(
  currentAssetsById: Map<string, Asset>,
  updatedAssets: Asset[],
  filesystem: MediaManagerFileSystem
): boolean {
  for (const updatedAsset of updatedAssets) {
    const currentAsset = currentAssetsById.get(updatedAsset.getUniqueIdentifier());
    if (!currentAsset) return false;
    if (!applyUpdatedAsset(currentAsset, updatedAsset, filesystem)) return false;
  }
  return true;
}

/**
 * Remove assets from both the filesystem and the asset library. Returns false as soon as the filesystem refuses
 * to delete an asset; the caller is expected to fall back to a full refetch in that case.
 */
export function applyDeletedAssets(
  deletedAssetUniqueIds: string[],
  filesystem: MediaManagerFileSystem,
  assetLibrary: MediaManagerAssetLibraryDto
): boolean {
  const deletedAssetIdSet = new Set(deletedAssetUniqueIds);
  for (const deletedAssetUniqueId of deletedAssetIdSet) {
    if (!filesystem.deleteAsset(deletedAssetUniqueId)) {
      return false;
    }
  }
  assetLibrary.assets = assetLibrary.assets.filter(asset => !deletedAssetIdSet.has(asset.getUniqueIdentifier()));
  return true;
}

/**
 * Insert a newly uploaded/created asset into the filesystem and asset library. Normalizes ParentFolderId to
 * 'ROOT' when the incoming asset is orphaned so the filesystem projection does not lose it.
 */
export function applyCreatedAsset(
  uploadedAsset: Asset,
  filesystem: MediaManagerFileSystem,
  assetLibrary: MediaManagerAssetLibraryDto
): boolean {
  const assetToCreate = window?.injector?.Deserialize?.instanceOf(Asset, uploadedAsset);
  assetToCreate.parentFolderId = assetToCreate.parentFolderId?.trim() || 'ROOT';
  const inFilesystem = filesystem.createAsset(assetToCreate);
  if (!inFilesystem) {
    return false;
  }
  assetLibrary.assets = [...(assetLibrary.assets ?? []), assetToCreate];
  return true;
}

/**
 * Route an uploaded asset either through the update path (already present) or the create path (net-new). Keeps
 * the two upstream flows (REST upload response and WS asset-uploaded event) sharing the same reconciliation.
 */
export function applyUploadedAssetToInMemoryState(
  uploadedAsset: Asset,
  filesystem: MediaManagerFileSystem,
  assetLibrary: MediaManagerAssetLibraryDto
): boolean {
  const existingAsset = findAssetInLibrary(uploadedAsset.getUniqueIdentifier(), assetLibrary);
  return existingAsset
    ? applyUpdatedAsset(existingAsset, uploadedAsset, filesystem)
    : applyCreatedAsset(uploadedAsset, filesystem, assetLibrary);
}
